/**
 * 프로젝트 편집 모달. cloud-api `PATCH /projects/:id`.
 *
 * Optimistic lock: project.version 을 함께 보내 서버 측에서 동시 수정 충돌
 * 검출. 충돌 시 ConflictException → "최신 데이터 받아온 뒤 다시" 안내.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  updateProject,
  type CommitStrategy,
  type Project,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const COMMIT_STRATEGIES: { value: CommitStrategy; label: string }[] = [
  { value: "PER_PLAN_MULTI_COMMIT", label: "PER_PLAN_MULTI_COMMIT (기본)" },
  { value: "PER_TASK_PR", label: "PER_TASK_PR" },
  { value: "PER_PLAN_SINGLE_COMMIT", label: "PER_PLAN_SINGLE_COMMIT" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onUpdated: (project: Project) => void;
  project: Project;
}

export function EditProjectDialog({
  open,
  onClose,
  onUpdated,
  project,
}: Props): JSX.Element | null {
  const [name, setName] = useState(project.name);
  const [slug, setSlug] = useState(project.slug);
  const [gitUrl, setGitUrl] = useState(project.gitUrl ?? "");
  const [defaultBranch, setDefaultBranch] = useState(project.defaultBranch);
  const [commitStrategy, setCommitStrategy] = useState<CommitStrategy>(
    project.commitStrategy,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // open 될 때마다 project 의 최신 값으로 reset (다른 프로젝트 클릭 후 다시 열기 대응)
  useEffect(() => {
    if (open) {
      setName(project.name);
      setSlug(project.slug);
      setGitUrl(project.gitUrl ?? "");
      setDefaultBranch(project.defaultBranch);
      setCommitStrategy(project.commitStrategy);
      setError(null);
      setSubmitting(false);
    }
  }, [open, project]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!SLUG_REGEX.test(slug)) {
      setError("slug 은 소문자/숫자/하이픈만 가능합니다");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await updateProject(project.id, {
        version: project.version,
        name: name !== project.name ? name : undefined,
        slug: slug !== project.slug ? slug : undefined,
        gitUrl: gitUrl !== (project.gitUrl ?? "") ? gitUrl : undefined,
        defaultBranch:
          defaultBranch !== project.defaultBranch ? defaultBranch : undefined,
        commitStrategy:
          commitStrategy !== project.commitStrategy ? commitStrategy : undefined,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "프로젝트 수정에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">프로젝트 수정</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              여기서 변경하면 데스크톱 앱의 sync 시 자동 반영됩니다.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="text-sm font-medium">
              프로젝트 이름
            </label>
            <Input
              id="edit-name"
              type="text"
              required
              maxLength={120}
              className="mt-1.5"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="edit-slug" className="text-sm font-medium">
              Slug
            </label>
            <Input
              id="edit-slug"
              type="text"
              required
              minLength={1}
              maxLength={60}
              className="mt-1.5 font-mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="edit-git" className="text-sm font-medium">
              Git URL <span className="text-muted-foreground">(선택)</span>
            </label>
            <Input
              id="edit-git"
              type="text"
              maxLength={500}
              className="mt-1.5"
              value={gitUrl}
              onChange={(e) => setGitUrl(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="edit-branch" className="text-sm font-medium">
              기본 브랜치
            </label>
            <Input
              id="edit-branch"
              type="text"
              maxLength={200}
              className="mt-1.5 font-mono"
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="edit-strategy" className="text-sm font-medium">
              커밋 전략
            </label>
            <select
              id="edit-strategy"
              value={commitStrategy}
              onChange={(e) => setCommitStrategy(e.target.value as CommitStrategy)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {COMMIT_STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              저장
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
